import { useState, type ReactNode } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { isEmpty } from "./Page";

/**
 * A record's page under its header: what it is about (lines, related lists) in the main
 * column, and beside it on a wide screen its facts and activity, staying in view as the
 * main column scrolls. On narrower screens the side follows the main column.
 */
export function RecordLayout({ main, aside }: { main: ReactNode; aside: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-8 xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className="min-w-0 space-y-8">{main}</div>
      <aside className="min-w-0 space-y-8 xl:sticky xl:top-24 xl:self-start">{aside}</aside>
    </div>
  );
}

/**
 * A record's facts as rows down the side: label, then value. Facts with nothing in them wait
 * behind one line, as on a full-width details block.
 */
export function Facts({ title, items, actions }: { title?: ReactNode; items: [ReactNode, ReactNode][]; actions?: ReactNode }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const filled = items.filter(([, v]) => !isEmpty(v));
  const hidden = items.length - filled.length;
  const shown = all || filled.length === 0 ? items : filled;
  return (
    <section className="min-w-0">
      {(title || actions) && (
        <div className="mb-1 flex min-h-8 items-center justify-between gap-3">
          {title && <h2 className="text-[15px] font-bold">{title}</h2>}
          {actions}
        </div>
      )}
      <dl className="divide-y divide-line-2">
        {shown.map(([k, v], i) => (
          <div key={i} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3 py-2.5">
            <dt className="text-[13px] text-ink-3">{k}</dt>
            <dd className="min-w-0 text-[14px] break-words">{isEmpty(v) ? <span className="text-ink-3">—</span> : v}</dd>
          </div>
        ))}
      </dl>
      {hidden > 0 && filled.length > 0 && (
        <button type="button" className="mt-2 text-[13px] text-ledger hover:underline" onClick={() => setAll((x) => !x)}>
          {all ? t("common.hideEmpty") : t("common.showEmpty", { count: hidden })}
        </button>
      )}
    </section>
  );
}

/** A record's headline numbers (total, paid, due) side by side in one framed strip: quiet labels over large figures. */
export function Figures({ items }: { items: { label: ReactNode; value: ReactNode; tone?: "danger" | "ledger" | "gold"; hint?: ReactNode }[] }) {
  const tone = { danger: "text-danger", ledger: "text-ledger-dark", gold: "text-[#8a6416]" };
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-[repeat(auto-fit,minmax(150px,1fr))] max-sm:[&>:last-child:nth-child(odd)]:col-span-2">
      {items.map((f, i) => (
        <div key={i} className="min-w-0 bg-surface px-4 py-3.5">
          <div className="truncate text-[12.5px] text-ink-3">{f.label}</div>
          <div className={clsx("mt-1 truncate text-[20px] leading-tight font-semibold", f.tone && tone[f.tone])}>{f.value}</div>
          {f.hint && <div className="mt-0.5 text-[12px] leading-snug text-ink-3">{f.hint}</div>}
        </div>
      ))}
    </div>
  );
}
